import { useState, useEffect, useCallback } from 'react';
import type { Vessel } from '../types';
import * as supa from '../lib/supabase';
import * as local from '../lib/db';
import { useOffline } from './useOffline';
import { useAuth } from './useAuth';

export function useVessels() {
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOnline } = useOffline();
  const { user } = useAuth();

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      let data: Vessel[] = [];
      if (isOnline) {
        data = await supa.getVessels();
        for (const v of data) await local.saveVesselLocal(v);
      } else {
        data = await local.getVesselsLocal();
      }
      setVessels(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load vessels');
      try {
        const localData = await local.getVesselsLocal();
        setVessels(localData);
      } catch { /* ignore */ }
    } finally {
      setLoading(false);
    }
  }, [isOnline, user]);

  useEffect(() => { load(); }, [load]);

  const addVessel = async (vessel: Omit<Vessel, 'id' | 'created_at'>) => {
    try {
      let newVessel: Vessel;
      if (isOnline && user) {
        newVessel = await supa.createVessel(vessel as Omit<Vessel, 'id' | 'created_at'>);
      } else {
        newVessel = { ...vessel, id: crypto.randomUUID(), created_at: new Date().toISOString(), created_by: user?.id } as Vessel;
      }
      await local.saveVesselLocal(newVessel);
      setVessels(prev => [...prev, newVessel].sort((a, b) => a.name.localeCompare(b.name)));
      return newVessel;
    } catch (e) {
      throw e instanceof Error ? e : new Error('Failed to add vessel');
    }
  };

  const deleteVessel = async (id: string) => {
    try {
      if (isOnline) await supa.deleteVessel(id);
      await local.deleteVesselLocal(id);
      setVessels(prev => prev.filter(v => v.id !== id));
    } catch (e) {
      throw e instanceof Error ? e : new Error('Failed to delete vessel');
    }
  };

  return { vessels, loading, error, refresh: load, addVessel, deleteVessel };
}
